'use client';

import { useMemo } from 'react';

import { FlatQuotationLine, QuotedColorGroup, SmallLotPricing } from '@ravago/shared/page-data/models/elements';
import { Address } from '@ravago/shared/page-data/models/elements/address.model';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Tabs } from '@ravago/shared/radiance/components/tabs/components/Tabs/Tabs';
import { useTranslations } from '@ravago/shared/translations/client';

import { useShoppingCartContext } from '../../../../../context/shopping-cart-context';
import { useCombinedPricingState } from '../../../../../hooks/use-combined-pricing-state';
import { hasMtsLocalLeadTime, hasQuotedColorLineMtsLocalLeadTime, hasSmallLotMtsLocalLeadTime } from '../../../../../utils/lead-time';
import {
   CombinedResolution,
   CombinedSuggestion,
   getDistinctQuoteQuantities,
   getLowestQuoteQuantity
} from '../../../../../utils/quote-pricing/combined-pricing.utils';
import { getQuantityUnitOfMeasure, getSuggestionRows, hasBracketRanges } from '../../../../../utils/quote-pricing/quote-selection.utils';
import { QuantitySelector } from '../../../../shared/quote-pricing/quantity-selector';
import { QuoteAddToCartButton } from '../../../../shared/quote-pricing/quote-add-to-cart-button';
import { QuoteLineSummary } from '../../../../shared/quote-pricing/quote-line-summary';
import { buildTabs } from '../../utils/pricing-detail-view-utils';

import { PricingContent } from './pricing-content';
import { SelectedColorHeader } from './selected-color-header';
import { SmallLotActiveTierSummary } from './small-lot-active-tier-summary';

interface Props {
   selectedColorGroup: QuotedColorGroup;
   flatLines: FlatQuotationLine[];
   productName: string;
   smallLotPricingForColorGroup: SmallLotPricing | null;
   quoteDetailRoute: string;
   hasMultipleSelections: boolean;
   bracketPricingEnabled?: boolean;
   leadTimeIndicators: LeadTimeIndicator[];
   leadTimeEnabled: boolean;
   selectedDeliveryAddressId: number | undefined;
   smallLotDefaultAddress?: Address;
   onEdit: () => void;
   onRequestQuote: () => void;
}

const addResolvedItemToCart = (
   resolution: CombinedResolution,
   smallLotPricingForColorGroup: SmallLotPricing | null,
   productName: string,
   addToCart: ReturnType<typeof useShoppingCartContext>['addToCart']
) => {
   if (resolution.kind === 'quote') {
      const { line } = resolution;
      addToCart({ type: 'QUOTATION', id: line.lineId }, line.lineOrderableProduct, line.linePackaging, {
         amount: resolution.quantity,
         unitOfMeasure: line.lineQuantityUOM
      });
      return;
   }

   if (resolution.kind === 'small-lot' && smallLotPricingForColorGroup) {
      const pricing = smallLotPricingForColorGroup;
      addToCart(
         { type: 'SMALL_LOT', id: pricing.ohmProductId.toString() },
         { id: pricing.ohmProductId, description: productName },
         pricing.packaging.name,
         {
            amount: resolution.quantity,
            unitOfMeasure: pricing.packaging.unitOfMeasure
         }
      );
   }
};

export const PricingDetailView = ({
   selectedColorGroup,
   flatLines,
   productName,
   smallLotPricingForColorGroup,
   quoteDetailRoute,
   hasMultipleSelections,
   bracketPricingEnabled = false,
   leadTimeIndicators,
   leadTimeEnabled,
   selectedDeliveryAddressId,
   smallLotDefaultAddress,
   onEdit,
   onRequestQuote
}: Props) => {
   const translate = useTranslations();
   const { addToCart, isAddingToCart } = useShoppingCartContext();

   const {
      inputValue,
      onInputChange,
      hasSettled,
      resolution,
      activeTab,
      setActiveTab,
      selectQuantity,
      hasSmallLot,
      singleQuoteNoSmallLot,
      netWeight
   } = useCombinedPricingState({ quoteLines: flatLines, smallLotPricing: smallLotPricingForColorGroup });

   const quoteQuantities = useMemo(
      () => getDistinctQuoteQuantities(flatLines, smallLotPricingForColorGroup?.packaging.netWeight),
      [flatLines, smallLotPricingForColorGroup?.packaging.netWeight]
   );
   const lowestQuote = getLowestQuoteQuantity(flatLines);
   const isBracketPricing = bracketPricingEnabled && hasBracketRanges(flatLines);
   const uom = getQuantityUnitOfMeasure(flatLines, smallLotPricingForColorGroup?.packaging.unitOfMeasure);
   const hideTabs = flatLines.length === 1 && !hasSmallLot;

   const isValid = resolution.kind !== 'suggestion';

   const suggestions = useMemo<CombinedSuggestion[]>(() => getSuggestionRows(resolution), [resolution]);

   const handleAddToCart = () => addResolvedItemToCart(resolution, smallLotPricingForColorGroup, productName, addToCart);

   const quotesBadge = `${lowestQuote ?? ''}${flatLines.length > 1 ? '+' : ''} ${uom}`;
   const listPricesBadge =
      hasSmallLot && netWeight !== undefined && lowestQuote !== null ? `${netWeight}–${lowestQuote - netWeight} ${uom}` : undefined;

   const tabs = buildTabs(hasSmallLot, quotesBadge, listPricesBadge, translate);

   const leadTimeResolution = useMemo(() => {
      if (!leadTimeEnabled) return undefined;

      if (resolution.kind === 'quote') {
         if (selectedDeliveryAddressId === undefined) return undefined;

         return hasMtsLocalLeadTime(leadTimeIndicators, {
            colorId: selectedColorGroup.colorId,
            producerColorCode: selectedColorGroup.producerColorCode,
            packagingGroupId: resolution.line.linePackagingGroupId,
            addressId: selectedDeliveryAddressId
         });
      }

      if (resolution.kind === 'small-lot' && smallLotPricingForColorGroup) {
         if (!smallLotDefaultAddress) return undefined;
         return hasSmallLotMtsLocalLeadTime(leadTimeIndicators, smallLotPricingForColorGroup, smallLotDefaultAddress);
      }

      return undefined;
   }, [
      leadTimeEnabled,
      leadTimeIndicators,
      resolution,
      selectedColorGroup,
      selectedDeliveryAddressId,
      smallLotPricingForColorGroup,
      smallLotDefaultAddress
   ]);

   const smallLotHasMtsLocal = useMemo(() => {
      if (!leadTimeEnabled || !smallLotPricingForColorGroup || !smallLotDefaultAddress) return undefined;
      return hasSmallLotMtsLocalLeadTime(leadTimeIndicators, smallLotPricingForColorGroup, smallLotDefaultAddress);
   }, [leadTimeEnabled, leadTimeIndicators, smallLotPricingForColorGroup, smallLotDefaultAddress]);

   const quoteLineMtsLocalMap = useMemo(() => {
      if (!leadTimeEnabled) return {};

      return Object.fromEntries(
         quoteQuantities.map(({ line }) => [
            line.lineId,
            hasQuotedColorLineMtsLocalLeadTime(leadTimeIndicators, selectedColorGroup, line.lineId, { bracketPricingEnabled })
         ])
      );
   }, [leadTimeEnabled, leadTimeIndicators, quoteQuantities, selectedColorGroup, bracketPricingEnabled]);

   return (
      <Box direction="column">
         <SelectedColorHeader
            colorName={selectedColorGroup.colorName}
            producerColorCode={selectedColorGroup.producerColorCode}
            hasMultipleSelections={hasMultipleSelections}
            onEdit={onEdit}
         />

         {!hideTabs && (
            <Tabs
               tabs={tabs}
               activeTab={activeTab === 'quotes' ? 0 : 1}
               visualizeChanges={hasSmallLot}
               onTabChange={(index) => setActiveTab(index === 0 ? 'quotes' : 'list-prices')}
            />
         )}

         <PricingContent
            activeTab={activeTab}
            hasSmallLot={hasSmallLot}
            isBracketPricing={isBracketPricing}
            smallLotPricingForColorGroup={smallLotPricingForColorGroup}
            smallLotHasMtsLocal={smallLotHasMtsLocal}
            resolution={resolution}
            onRequestQuote={onRequestQuote}
            quoteQuantities={quoteQuantities}
            quoteLineMtsLocalMap={quoteLineMtsLocalMap}
            selectQuantity={selectQuantity}
         />

         {!singleQuoteNoSmallLot && (
            <QuantitySelector
               value={inputValue}
               suffix={uom}
               error={hasSettled && resolution.kind === 'suggestion' && inputValue !== ''}
               suggestions={hasSettled && inputValue !== '' ? suggestions : []}
               smallLotPackaging={smallLotPricingForColorGroup?.packaging}
               onChange={onInputChange}
               onSelect={selectQuantity}
               id="combined-quantity-input"
            />
         )}

         {resolution.kind === 'quote' && (
            <QuoteLineSummary
               line={resolution.line}
               quoteDetailRoute={quoteDetailRoute}
               selectedQuantity={resolution.quantity}
               hasMtsLocal={leadTimeResolution}
               showValidUntil
            />
         )}
         {resolution.kind === 'small-lot' && smallLotPricingForColorGroup && (
            <SmallLotActiveTierSummary
               tier={resolution.tier}
               selectedQuantity={resolution.quantity}
               packaging={smallLotPricingForColorGroup.packaging}
               hasMtsLocal={leadTimeResolution}
            />
         )}

         <QuoteAddToCartButton hasOrderingPermission={true} disabled={!isValid || isAddingToCart} onAddToCart={handleAddToCart} />
      </Box>
   );
};
